import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Package, ArrowRight, CheckCircle } from "lucide-react";

const LfbHistorySection = ({ lead }) => {
  const lfbHistory = lead?.lfb_history || [];
  const currentLfbId = lead?.lfb_id;

  if (lfbHistory.length === 0 && !currentLfbId) return null;

  const getStatusBadge = (entry) => {
    if (entry.status === 'lfb_created' || entry.lfb_id) {
      return <Badge className="bg-emerald-100 text-emerald-700 text-xs">LFB Created</Badge>;
    }
    return <Badge className="bg-amber-100 text-amber-700 text-xs">Pushed to Pick Pool</Badge>;
  };

  return (
    <Card className="border-slate-200" data-testid="lfb-history-section">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Package className="w-5 h-5 text-blue-600" />
          LFB History
          {currentLfbId && (
            <Badge className="bg-blue-100 text-blue-700 font-mono text-xs ml-2">{currentLfbId}</Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-slate-200" />

          <div className="space-y-4">
            {lfbHistory.map((entry, idx) => (
              <div key={idx} className="relative flex gap-4 pl-10" data-testid={`lfb-entry-${idx}`}>
                {/* Timeline dot */}
                <div className={`absolute left-2.5 w-3 h-3 rounded-full border-2 ${
                  entry.lfb_id ? 'bg-emerald-500 border-emerald-500' : 'bg-amber-400 border-amber-400'
                }`} style={{ top: '6px' }} />

                <div className="flex-1 bg-slate-50 rounded-lg p-3 border border-slate-100">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {getStatusBadge(entry)}
                      {entry.lfb_id && (
                        <span className="text-xs font-mono text-blue-600 font-semibold">{entry.lfb_id}</span>
                      )}
                    </div>
                    <span className="text-xs text-slate-400">
                      {entry.pushed_at ? new Date(entry.pushed_at).toLocaleDateString('en-IN', {
                        day: '2-digit', month: 'short', year: 'numeric'
                      }) : ''}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                    {entry.fabric_type && (
                      <div>
                        <span className="text-slate-400">Fabric:</span>{' '}
                        <span className="text-slate-700 font-medium">{entry.fabric_type}</span>
                      </div>
                    )}
                    {entry.fabric_category && (
                      <div>
                        <span className="text-slate-400">Category:</span>{' '}
                        <span className="text-slate-700 font-medium">{entry.fabric_category}</span>
                      </div>
                    )}
                    {entry.material_1 && (
                      <div>
                        <span className="text-slate-400">Material:</span>{' '}
                        <span className="text-slate-700 font-medium">{entry.material_1} {entry.material_1_percentage ? `(${entry.material_1_percentage}%)` : ''}</span>
                      </div>
                    )}
                    {entry.gsm && (
                      <div>
                        <span className="text-slate-400">GSM:</span>{' '}
                        <span className="text-slate-700 font-medium">{entry.gsm}</span>
                      </div>
                    )}
                    {entry.width_inches && (
                      <div>
                        <span className="text-slate-400">Width:</span>{' '}
                        <span className="text-slate-700 font-medium">{entry.width_inches}"</span>
                      </div>
                    )}
                    {entry.quantity && (
                      <div>
                        <span className="text-slate-400">Qty:</span>{' '}
                        <span className="text-slate-700 font-medium">{entry.quantity} {entry.quantity_unit || 'mtrs'}</span>
                      </div>
                    )}
                    {entry.color && (
                      <div>
                        <span className="text-slate-400">Color:</span>{' '}
                        <span className="text-slate-700 font-medium">{entry.color}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2 mt-2 text-xs text-slate-400">
                    <span>By: {entry.pushed_by || 'Unknown'}</span>
                    {entry.lfb_submitted_at && (
                      <>
                        <ArrowRight className="w-3 h-3" />
                        <CheckCircle className="w-3 h-3 text-emerald-500" />
                        <span>LFB submitted {new Date(entry.lfb_submitted_at).toLocaleDateString('en-IN')}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {/* Show current LFB if no lfb_history but lfb_id exists */}
            {lfbHistory.length === 0 && currentLfbId && (
              <div className="relative flex gap-4 pl-10">
                <div className="absolute left-2.5 w-3 h-3 rounded-full border-2 bg-emerald-500 border-emerald-500" style={{ top: '6px' }} />
                <div className="flex-1 bg-emerald-50 rounded-lg p-3 border border-emerald-100">
                  <div className="flex items-center gap-2">
                    <Badge className="bg-emerald-100 text-emerald-700 text-xs">LFB Created</Badge>
                    <span className="text-xs font-mono text-blue-600 font-semibold">{currentLfbId}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Pushed to Pick Pool on {lead?.pick_pool_pushed_at ? new Date(lead.pick_pool_pushed_at).toLocaleDateString('en-IN') : 'N/A'}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default LfbHistorySection;
